import { batch, signal, Signal } from '@preact/signals-react';
import { injectable } from 'inversify';

import { ProfileManagerState, ProfileManagerStatus } from '@vp/manager/profile/core/interface/ProfileManagerState';
import { ProfileAccessModel } from '@vp/profile/core/model/ProfileAccessModel';
import { ProfileLiveMediaModel } from '@vp/profile/core/model/ProfileLiveMediaModel';
import { ProfileModel } from '@vp/profile/core/model/ProfileModel';
import { ProfilePhotoModel } from '@vp/profile/core/model/ProfilePhotoModel';

@injectable()
export class SignalsProfileManagerState implements ProfileManagerState {
  readonly profiles: Signal<ProfileModel[]> = signal([]);
  readonly status: Signal<ProfileManagerStatus> = signal('initial');
  readonly active: Signal<ProfileModel | null> = signal(null);

  updateLiveMedia(liveMedia: ProfileLiveMediaModel): void {
    const updated = { ...this.getCurrentProfile() };
    updated.liveMedia = new ProfileLiveMediaModel({ ...updated.liveMedia, ...liveMedia } as ProfileLiveMediaModel);
    return this.update(updated);
  }

  hasPhotoAnimationAttempts(): boolean {
    const { liveMedia } = this.getCurrentProfile();
    return liveMedia.photosUsageCounter < liveMedia.maxPhotosCount;
  }

  hasProfileImageAnimationAttempts(): boolean {
    const { liveMedia } = this.getCurrentProfile();
    return liveMedia.profileImageUsageCounter < liveMedia.maxProfileImageCount;
  }

  updatePhoto(photo: ProfilePhotoModel): void {
    const updated = { ...this.getCurrentProfile() };

    if (photo.id === updated.image.id) {
      updated.image = photo;
    } else {
      updated.photos = updated.photos.map(p => {
        return p.id === photo.id ? photo : p;
      });
    }

    return this.update(updated);
  }

  getPhotoOfActiveProfileBy(id: string): ProfilePhotoModel {
    const current = this.getCurrentProfile();
    const photo = current.photos.find(photo => photo.id === id);
    if (!photo) throw new Error(`Photo ${id} not found.`);
    return photo;
  }

  updateAccess(access: ProfileAccessModel): void {
    const current = this.getCurrentProfile();
    const updated = { ...current, access };
    this.update(updated);
  }

  completed(status: ProfileManagerStatus, profiles?: ProfileModel[]): void {
    this.status.value = status;
    this.profiles.value = profiles ?? this.profiles.value;
  }

  loading(): void {
    this.status.value = 'loading';
  }

  getById(id: string): ProfileModel | null {
    return this.profiles.value.find(profile => profile.id === id) ?? null;
  }

  setActive(profile: ProfileModel): void {
    this.active.value = profile;
  }

  update(updatedProfile: ProfileModel): void {
    if (!this.getById(updatedProfile.id)) {
      return void (this.profiles.value = this.profiles.value.concat(updatedProfile));
    }

    this.profiles.value = this.profiles.value.map(profile => {
      return profile.id === updatedProfile.id ? updatedProfile : profile;
    });

    if (updatedProfile.id === this.active.value?.id) {
      this.active.value = updatedProfile;
    }
  }

  reset(): void {
    batch(() => {
      this.profiles.value = [];
      this.status.value = 'initial';
      this.active.value = null;
    });
  }

  private getCurrentProfile(): ProfileModel {
    const current = this.active.value;
    if (!current) throw new Error('No active profile.');
    return current;
  }
}
